import { UcoastEl } from '@/scripts/core/UcoastEl'
import { TsDOM as q } from '@/scripts/core/TsDOM';
import { setLocalStorageWithExpiry, getLocalStorageWithExpiry, removeLocalStorage } from '@/scripts/core/global';

const STORAGE_KEY = 'lobby-radio-state'
const TIME_UPDATE_THROTTLE = 1000
const EXPIRY_MS = 24 * 60 * 60 * 1000 // 24 hours

interface RadioState {
    isPlaying: boolean
    currentTime: number
    audioSrc: string
}

export class LobbyRadio extends UcoastEl {
    static htmlSelector = 'lobby-radio'
    audio: HTMLAudioElement
    control: HTMLButtonElement
    timerWrapper: HTMLElement
    lastTimeUpdate: number = 0
    stateRestored: boolean = false

    constructor() {
        super()
        this.audio = q.rs('audio', this)
        this.control = q.rs('.header__lobby-radio-control', this)
        this.timerWrapper = q.rs('.header__lobby-radio-timer', this)
        this.control.addEventListener('click', this.onClickControl.bind(this))
        this.audio.addEventListener('timeupdate', this.onTimeUpdate.bind(this))
        this.audio.addEventListener('ended', this.onEnded.bind(this))
        this.audio.addEventListener('loadedmetadata', this.onLoadedMetadata.bind(this))
        this.audio.addEventListener('canplay', this.onLoadedMetadata.bind(this))
        
        window.addEventListener('beforeunload', this.saveState.bind(this))
        window.addEventListener('pagehide', this.saveState.bind(this))
    }

    override connectedCallback() {
        super.connectedCallback()
        if (!this.stateRestored) {
            if (this.audio.readyState >= HTMLMediaElement.HAVE_METADATA) {
                this.restoreState()
                this.updateTimerDisplay()
            } else {
                this.audio.preload = 'metadata'
                this.audio.load()
                setTimeout(() => {
                    if (!this.stateRestored && this.audio.readyState >= HTMLMediaElement.HAVE_METADATA) {
                        this.restoreState()
                        this.updateTimerDisplay()
                    }
                }, 500)
            }
        }
    }

    getAudioSrc(): string {
        const source = this.audio.querySelector('source')
        return source?.getAttribute('src') || ''
    }

    getStoredState(): RadioState | null {
        return getLocalStorageWithExpiry<RadioState>(STORAGE_KEY)
    }

    saveState() {
        const state: RadioState = {
            isPlaying: this.classList.contains('playing'),
            currentTime: this.audio.currentTime,
            audioSrc: this.getAudioSrc()
        }
        setLocalStorageWithExpiry(STORAGE_KEY, state, EXPIRY_MS)
    }

    restoreState() {
        if (this.stateRestored) return
        
        const stored = this.getStoredState()
        if (!stored) {
            this.stateRestored = true
            return
        }

        const currentSrc = this.getAudioSrc()
        if (stored.audioSrc !== currentSrc) {
            removeLocalStorage(STORAGE_KEY)
            this.stateRestored = true
            return
        }

        if (this.audio.duration && stored.currentTime > 0 && stored.currentTime < this.audio.duration) {
            this.audio.currentTime = stored.currentTime
        } else if (stored.currentTime > 0 && this.audio.duration) {
            this.audio.currentTime = Math.min(stored.currentTime, this.audio.duration - 0.1)
        }

        if (stored.isPlaying) {
            this.classList.add('playing')
            this.audio.play().catch(() => {
                this.classList.remove('playing')
            })
        }
        
        this.stateRestored = true
    }

    onLoadedMetadata() {
        if (!this.stateRestored && this.audio.readyState >= HTMLMediaElement.HAVE_METADATA) {
            this.restoreState()
        }
        this.updateTimerDisplay()
    }

    updateTimerDisplay() {
        const currentTime = Math.floor(this.audio.currentTime)
        const duration = isNaN(this.audio.duration) ? 0 : Math.floor(this.audio.duration)
        
        if (duration > 0) {
            this.timerWrapper.textContent = this.timeFormat(currentTime) + ' / ' + this.timeFormat(duration)
        } else if (!this.classList.contains('playing')) {
            this.timerWrapper.textContent = this.timerWrapper.dataset.text || ''
        }
    }

    addAnimation() {
        this.timerWrapper.classList.add('scroll-trigger', 'animate--fade-in')
        this.timerWrapper.addEventListener('animationend', () => {
            this.timerWrapper.classList.remove('scroll-trigger', 'animate--fade-in')
        }, { once: true });
    }

    onClickControl(e: Event) {
        e.preventDefault()
        if(this.classList.contains('playing')) {
            this.classList.remove('playing')
            this.audio.pause()
            this.saveState()
            setTimeout(() => {
                this.timerWrapper.textContent = this.timerWrapper.dataset.text || ''
                this.addAnimation()
            }, 50)
        } else {
            this.classList.add('playing')
            this.audio.play()
            this.saveState()
            setTimeout(() => {
                this.addAnimation()
            }, 150);
        }
    }

    onTimeUpdate() {
        this.updateTimerDisplay()
        
        const duration = isNaN(this.audio.duration) ? 0 : Math.floor(this.audio.duration)
        const now = Date.now()
        if (now - this.lastTimeUpdate >= TIME_UPDATE_THROTTLE && duration > 0) {
            this.saveState()
            this.lastTimeUpdate = now
        }
    }

    onEnded() {
        this.classList.remove('playing')
        this.saveState()
        setTimeout(() => {
            this.timerWrapper.textContent = this.timerWrapper.dataset.text || ''
            this.addAnimation()
        }, 50)
    }

    timeFormat(timeInSeconds: number) {
        const minutes = Math.floor(timeInSeconds / 60).toString().padStart(2, '0');
        const seconds = (timeInSeconds % 60).toString().padStart(2, '0');
        return `${minutes}:${seconds}`;
    }
}
